import {BehaviorSubject, Subject} from 'rxjs';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import { sortBy } from 'lodash-es';
import {CancellationInvoice} from '../../models';
import { SearchAddonDataService } from '../../services/search-addon-data.service';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import { extractIri } from '../../../shared/utilities/objects.utility';
import * as moment from 'moment';
import {ActivatedRoute, Router} from "@angular/router";
import {LocalStorageService} from "../../../shared/services";
import {CancellationInvoicesService} from "../../services";
import {getUuidFromIri} from "../../../shared/utilities/strings.utility";
import {CancellationInvoicesActions, PayableInvoicesActions} from "../../store";
import {Store} from "@ngrx/store";
import {ApplicationState} from "../../../application-state/store";

@Component({
  selector: 'app-cancellation-invoices',
  styleUrls: ['cancellation-invoices-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="col pt-3">
      <div class="row ">
        <div class="col">
          <app-cancellation-invoices-filter
            *ngIf="searchBoxVisible"
            [items]="cancellationInvoices$ | async"
            (updateFilter)="handleFilterFormAction($event)"
          ></app-cancellation-invoices-filter>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <div class="table-paginator-group pos-relative">
            <app-loading-overlay *ngIf="loadingTable$|async"></app-loading-overlay>
            <app-cancellation-invoices-list
              *ngIf="cancellationInvoices$ | async"
              [items]="cancellationInvoices$ | async"
            ></app-cancellation-invoices-list>

            <app-paginator-unstyled
              [totalItems]="cancellationInvoicesCount"
              [pageSize]="pageSize"
              [pageIndex]="currentPage - 1"
              [pageSizeOptions]="pageSizeOptions"
              (handleUpdatePageOrSize)="handleUpdatePageOrSize($event)"
              [showFirstLastButtons]="true"
            ></app-paginator-unstyled>
          </div>
        </div>
      </div>
    </div>
  `
})
export class CancellationInvoicesViewComponent implements OnInit, OnDestroy {
  cancellationInvoices$: BehaviorSubject<Array<CancellationInvoice>> = new BehaviorSubject<Array<CancellationInvoice>>([]);
  currentPage = 1;
  pageSize = 30;
  pageSizeOptions = [5, 10, 15, 20, 25];
  loadingTable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  params$: BehaviorSubject<{ [key: string]: any }> = new BehaviorSubject<{ [p: string]: any }>({
    itemsPerPage: this.pageSize,
    page: this.currentPage,
    'order[createdAt]': 'desc',
  });

  activeFilter: { [p: string]: string } = {};
  searchBoxVisible = false;
  filterString = '';
  onDestroy$: Subject<any> = new Subject<any>();
  cancellationInvoicesCount: number;
  subscriptionList: any;

  constructor(
    private store$: Store<ApplicationState>,
    private router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private localStorageService: LocalStorageService,
    private searchAddonDataService: SearchAddonDataService,
    private cancellationInvoicesService: CancellationInvoicesService
  ) {
    this.currentPage = 1;
  }

  ngOnInit(): void {
    this.loadCancelationInvoices();
    const filters = this.localStorageService.getObjectByKey('filters', 'cancellation_invoices');
    if(filters) {
      this.handleFilterFormAction(filters);
    }

    this.searchAddonDataService
      .getObservable()
      .pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe(data => {
        this.filterString = data.filterString;
        this.searchBoxVisible = data.searchBoxVisible;
        this.activeFilter = data.activeFilter;
        this.cd.detectChanges();
      });

    this.route.queryParams.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
      this.currentPage = params['page'] ? +params['page'] : 1;
      this.updateParams();
    });

    this.searchAddonDataService
      .getObservable()
      .pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe(data => {
        this.handleSearchInput(data.filterString);
        this.cd.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  updateUrl() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: this.currentPage },
      queryParamsHandling: 'merge'
    });
  }

  handleUpdatePageOrSize(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.updateParams();
    this.loadCancelationInvoices();
    this.updateUrl();
  }
  handleFilterFormAction(filters: any): void {
    const params = {
      department: null,
      orderNumber: null,
      'grossTotal.value[gte]': null,
      'grossTotal.value[lte]': null,
      'createdAt[strictly_after]': null,
      'createdAt[strictly_before]': null,
    };

    if (filters?.department) {
      params['issuer.uuid'] = getUuidFromIri(extractIri(filters.department));
    } else {
      params['issuer.uuid'] = null;
    }
    if (filters?.orderNumber) {
      params.orderNumber =  String(filters.orderNumber);
    }

    if (filters?.maxGrossPrice) {
      params['grossTotal.value[lte]'] = filters.exactGrossPrice;
      params['grossTotal.value[gte]'] = filters.exactGrossPrice;
    }

    if (filters?.createdAtMin) {
      params['createdAt[strictly_after]'] = filters.createdAtMin;
    }

    if (filters?.createdAtMax) {
      params['createdAt[strictly_before]'] = filters.createdAtMax;
    }

    this.params$.next({
      ...this.params$.getValue(),
      ...params
    });
    this.updateParams();
    this.loadCancelationInvoices();
  }
  loadCancelationInvoices(): void {
    if (this.subscriptionList) {
      this.subscriptionList.unsubscribe();
    }
    this.loadingTable$.next(true);
    this.subscriptionList = this.cancellationInvoicesService.readCancellationInvoices(this.currentPage, this.params$.getValue())
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(response => {
        this.loadingTable$.next(false);
        this.cancellationInvoicesCount = response['hydra:totalItems'];
        this.cancellationInvoices$.next(this.sortInvoices(response['hydra:member']));
      }, (fail) => {
        this.loadingTable$.next(false);
        console.error(fail);
      });
  }
  sortInvoices(
    toBeSortedInvoices: CancellationInvoice[]
  ): CancellationInvoice[] {
    // return sortBy(toBeSortedInvoices, (e: CancellationInvoice) => e.cancellationInvoiceNumber);
    return sortBy(
      toBeSortedInvoices,
      e =>
        moment(e.bookedAt)
          .toDate()
          .getTime() * -1
    );
  }

  handleSearchInput(term: string): void {
    this.params$.next({
      ...this.params$.getValue(),
      fulltext_search: term ? term : ''
    });

    this.store$.dispatch(
      CancellationInvoicesActions.ReadCancellationInvoices({
        page: this.currentPage,
        params: this.params$.getValue()
      })
    );
    this.loadCancelationInvoices();
  }

  private updateParams(): void {
    if (this.searchBoxVisible === false) {
      this.params$.next({
        ...this.params$.getValue(),
        'order[createdAt]': 'desc',
        fulltext_search: this.params$.getValue().hasOwnProperty('fulltext_search') ? this.params$.getValue().fulltext_search : ''
      });
    }
    this.params$.next({
      ...this.params$.getValue(),
      itemsPerPage: this.pageSize,
      page: this.currentPage
    });
  }
}
